import React, { useState } from 'react';
import axios from 'axios';
import { X, Phone, User, MapPin, Building2, Sparkles, Check } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from '../hooks/use-toast';
import { useLanguage } from '../context/LanguageContext';
import { trackDemoRequest } from '../lib/analytics';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const propertyTypeOptions = ['Co-Living', 'Hostel/PG', 'Flat', 'Studio'];
const propKeyMap = {
  'Co-Living': 'prop.coliving',
  'Hostel/PG': 'prop.hostel',
  'Flat': 'prop.flat',
  'Studio': 'prop.studio'
};

const UpgradeToSmartModal = ({ open, onClose }) => {
  const { t } = useLanguage();
  const [form, setForm] = useState({
    name: '',
    mobile: '',
    city: '',
    property_type: 'Co-Living',
    beds: ''
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  if (!open) return null;

  const update = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.mobile || form.mobile.length < 10) {
      toast({ title: t('um.invalid_mobile'), variant: 'destructive' });
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/demo-requests`, {
        property_type: form.property_type,
        mobile: form.mobile,
        name: form.name,
        city: form.city,
        beds: form.beds,
        source: 'upgrade-to-smart-property'
      });
    } catch (_) { /* still success UX */ }
    trackDemoRequest({
      source: 'upgrade-to-smart-property',
      property_type: form.property_type,
      mobile: form.mobile,
      name: form.name,
      city: form.city,
      beds: form.beds
    });
    setSuccess(true);
    setLoading(false);
  };

  const handleClose = () => {
    setSuccess(false);
    setForm({ name: '', mobile: '', city: '', property_type: 'Co-Living', beds: '' });
    onClose();
  };

  return (
    <div
      data-testid="upgrade-modal-backdrop"
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-in fade-in duration-200"
      onClick={handleClose}
    >
      <div
        data-testid="upgrade-modal"
        className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto relative animate-in zoom-in-95 duration-200"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          data-testid="upgrade-modal-close"
          onClick={handleClose}
          className="absolute top-4 right-4 p-2 rounded-full hover:bg-gray-100 text-gray-500 hover:text-gray-900 transition-colors z-10"
          aria-label="Close"
        >
          <X size={20} />
        </button>

        {success ? (
          <div className="p-10 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-5">
              <Check className="text-green-600" size={32} />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-2">{t('um.success_h')}</h3>
            <p className="text-gray-600 mb-6">
              {t('um.success_p_a')} <span className="font-semibold">{form.mobile}</span> {t('um.success_p_b')}
            </p>
            <Button
              data-testid="upgrade-modal-done-btn"
              onClick={handleClose}
              className="bg-blue-600 hover:bg-blue-700 text-white rounded-lg px-8"
            >
              {t('um.done')}
            </Button>
          </div>
        ) : (
          <>
            <div className="bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-700 p-6 rounded-t-2xl text-white">
              <div className="inline-flex items-center gap-1.5 bg-white/20 backdrop-blur px-2.5 py-1 rounded-full text-[11px] font-semibold mb-3">
                <Sparkles size={12} />
                {t('um.badge')}
              </div>
              <h2 className="text-2xl font-bold leading-tight mb-1">{t('um.h2')}</h2>
              <p className="text-blue-100 text-sm">{t('um.sub')}</p>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="text-xs font-semibold text-gray-700 mb-1.5 flex items-center gap-1.5">
                  <User size={13} /> {t('um.name')}
                </label>
                <Input
                  data-testid="upgrade-form-name"
                  type="text"
                  placeholder={t('um.name_ph')}
                  value={form.name}
                  onChange={(e) => update('name', e.target.value)}
                  className="rounded-lg border-gray-300 focus:border-blue-600"
                />
              </div>

              <div>
                <label className="text-xs font-semibold text-gray-700 mb-1.5 flex items-center gap-1.5">
                  <Phone size={13} /> {t('um.mobile')} <span className="text-red-500">*</span>
                </label>
                <Input
                  data-testid="upgrade-form-mobile"
                  type="tel"
                  placeholder={t('um.mobile_ph')}
                  value={form.mobile}
                  onChange={(e) => update('mobile', e.target.value.replace(/\D/g, '').slice(0, 10))}
                  className="rounded-lg border-gray-300 focus:border-blue-600"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-semibold text-gray-700 mb-1.5 flex items-center gap-1.5">
                    <MapPin size={13} /> {t('um.city')}
                  </label>
                  <Input
                    data-testid="upgrade-form-city"
                    type="text"
                    placeholder={t('um.city_ph')}
                    value={form.city}
                    onChange={(e) => update('city', e.target.value)}
                    className="rounded-lg border-gray-300 focus:border-blue-600"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-700 mb-1.5 flex items-center gap-1.5">
                    <Building2 size={13} /> {t('um.beds')}
                  </label>
                  <Input
                    data-testid="upgrade-form-beds"
                    type="text"
                    placeholder={t('um.beds_ph')}
                    value={form.beds}
                    onChange={(e) => update('beds', e.target.value)}
                    className="rounded-lg border-gray-300 focus:border-blue-600"
                  />
                </div>
              </div>

              <div>
                <label className="text-xs font-semibold text-gray-700 mb-1.5 block">
                  {t('um.proptype')}
                </label>
                <div className="grid grid-cols-4 gap-2">
                  {propertyTypeOptions.map((type) => (
                    <button
                      key={type}
                      type="button"
                      data-testid={`upgrade-form-type-${type}`}
                      onClick={() => update('property_type', type)}
                      className={`py-2 px-1 rounded-lg border text-xs font-semibold transition-all ${
                        form.property_type === type
                          ? 'border-blue-600 bg-blue-50 text-blue-700'
                          : 'border-gray-200 bg-white text-gray-600 hover:border-blue-300'
                      }`}
                    >
                      {t(propKeyMap[type])}
                    </button>
                  ))}
                </div>
              </div>

              <Button
                data-testid="upgrade-form-submit"
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white rounded-lg py-6 text-base font-bold disabled:opacity-60"
              >
                {loading ? t('um.submitting') : t('um.submit')}
              </Button>

              <p className="text-[11px] text-center text-gray-500">{t('um.consent')}</p>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default UpgradeToSmartModal;
